import { useState } from 'react';
import { api } from '../lib/api';
import type { Device } from '../lib/api';
import { Empty, Notice, Panel, formatDateTime } from './ui';

/**
 * Panels that report what a device is ACTUALLY doing, as opposed to what it was
 * asked to do.
 *
 * All three exist to close the same gap: the dashboard used to render intent —
 * a policy was assigned, a kiosk was requested, a device was last seen — and
 * leave the reader to assume the device complied. These render the device's own
 * reported observations instead, and say plainly when there are none.
 */

/**
 * Why the device is or is not answering.
 *
 * The server never prints a bare "OFFLINE" here. It names the most likely
 * explanation, the confidence behind it, the alternatives that fit the same
 * evidence, and every signal it used — because a dashboard that says "offline"
 * and nothing else leaves the reader to guess between a flat battery and a
 * factory reset.
 */
export function ReachabilityPanel({ device }: { device: Device }) {
  const r = device.reachability;
  if (!r) return null;

  const kind =
    r.severity === 'critical'
      ? 'error'
      : r.severity === 'high' || r.severity === 'warning'
        ? 'warning'
        : 'success';

  return (
    <Panel title="Reachability analysis">
      <Notice kind={kind}>
        <strong>
          {r.headline}
          <span className="badge muted" style={{ fontSize: 11, marginLeft: 8 }}>
            {r.classification}
          </span>
          <span
            className={`badge ${
              r.confidence === 'HIGH' ? 'ok' : r.confidence === 'MEDIUM' ? 'warn' : 'muted'
            }`}
            style={{ fontSize: 11, marginLeft: 4 }}
          >
            {r.confidence} CONFIDENCE
          </span>
        </strong>
        <div>{r.conclusion}</div>
      </Notice>

      {r.alternatives.length > 0 && (
        <>
          <h4 style={{ marginBottom: 4 }}>Other explanations that fit the same evidence</h4>
          <ul className="small muted" style={{ marginTop: 0 }}>
            {r.alternatives.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </>
      )}

      <h4 style={{ marginBottom: 6 }}>Signals used</h4>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Signal</th>
              <th>Value</th>
              <th>Weighed</th>
            </tr>
          </thead>
          <tbody>
            {r.signals.map((s) => (
              <tr key={s.label}>
                <td>{s.label}</td>
                <td className="mono small">{s.value}</td>
                <td>
                  {s.supporting ? (
                    <span className="badge info">SUPPORTS VERDICT</span>
                  ) : (
                    <span className="muted small">context</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="small muted" style={{ marginBottom: 0 }}>
        The server cannot see a powered-off device or one on a dead network — those two look
        identical from here. Any conclusion above LOW confidence is backed by a positive signal,
        never by silence alone.
      </p>
    </Panel>
  );
}

/**
 * What the device reported doing with each control of its policy.
 *
 * This is the difference between "the policy applied" and "the camera
 * restriction is actually in force". The agent reads most controls back from
 * the platform after setting them, so FAILED here means Android accepted a call
 * and then did not honour it.
 */
export function PolicyResultPanel({ device }: { device: Device }) {
  const results = device.policyControlResults ?? [];
  if (!device.policy) return null;

  if (results.length === 0) {
    return (
      <Panel title="Policy control results">
        <Empty>
          This device has not reported per-control results yet. It will on its next policy
          acknowledgement.
        </Empty>
      </Panel>
    );
  }

  const counts = {
    applied: results.filter((c) => c.status === 'APPLIED').length,
    failed: results.filter((c) => c.status === 'FAILED').length,
    unsupported: results.filter((c) => c.status === 'UNSUPPORTED').length,
    skipped: results.filter((c) => c.status === 'SKIPPED').length,
  };

  const badgeFor = (status: string) =>
    status === 'APPLIED'
      ? 'ok'
      : status === 'FAILED'
        ? 'danger'
        : status === 'UNSUPPORTED'
          ? 'warn'
          : 'muted';

  const overall = device.policyOverallStatus ?? 'UNKNOWN';
  const overallOk = overall === 'APPLIED' || overall === 'SUCCESS';
  const overallFail = overall === 'FAILED' || overall === 'FAIL';

  return (
    <Panel
      title="Policy control results"
      actions={
        <span
          className={`badge ${
            overallOk ? 'ok' : overallFail ? 'danger' : 'warn'
          }`}
        >
          {overall}
        </span>
      }
    >
      <p className="small muted" style={{ marginTop: 0 }}>
        Reported by the device at {formatDateTime(device.lastPolicyAckAt)}. {counts.applied} applied
        · {counts.failed} failed · {counts.unsupported} unsupported · {counts.skipped} not
        requested.
      </p>

      {counts.failed > 0 && (
        <Notice kind="error">
          <strong>{counts.failed} control(s) were accepted by Android and are not in force.</strong>
          <div>
            The agent reported these as FAILED after a set or a restore. Treat this device as
            NOT matching its assigned policy until they succeed. Switching back to TEST after
            kiosk must show hide/restriction rows as SKIPPED, not FAILED.
          </div>
        </Notice>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Control</th>
              <th>Result</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {results.map((c) => (
              <tr key={c.key}>
                <td className="mono small">{c.key}</td>
                <td>
                  <span className={`badge ${badgeFor(c.status)}`}>{c.status}</span>
                </td>
                <td className="small">{c.detail ?? <span className="muted">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/**
 * Remote factory reset.
 *
 * Deliberately hostile to use: kept out of the ordinary action row, requires the
 * device's own name to be typed, and requires the super_admin role — which the
 * server enforces independently of anything this component does, because a
 * disabled button is not an authorisation control. A refused attempt is audited
 * as loudly as a successful one.
 */
export function WipePanel({
  device,
  onWipe,
}: {
  device: Device;
  onWipe: (command: string, confirmText?: string, extra?: Record<string, unknown>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [wipeExternalStorage, setWipeExternalStorage] = useState(false);

  const close = () => {
    setOpen(false);
    setTyped('');
  };

  return (
    <Panel title="Destructive actions">
      {!open ? (
        <>
          <p className="small muted" style={{ marginTop: 0 }}>
            Remotely factory reset this tablet. Everything on it is erased, management is removed,
            and re-enrolling requires a physical QR scan at the setup wizard. There is no undo and
            no partial recovery.
          </p>
          <button className="danger" onClick={() => setOpen(true)}>
            Wipe {device.displayName}…
          </button>
        </>
      ) : (
        <>
          <Notice kind="error">
            <strong>This erases the device. It cannot be undone.</strong>
            <div>
              All exam data, the management agent and the enrolment are destroyed, and the device
              returns to the out-of-box setup wizard. If the device is offline the command waits in
              the queue and runs the moment it reconnects — which may be days later.
            </div>
          </Notice>

          <label>
            <span>
              Type the device name <code>{device.displayName}</code> to confirm
            </span>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={device.displayName}
            />
          </label>

          <label className="inline">
            <input
              type="checkbox"
              checked={wipeExternalStorage}
              onChange={(e) => setWipeExternalStorage(e.target.checked)}
            />
            <span>Also erase external storage (SD card)</span>
          </label>

          <div className="actions">
            <button onClick={close}>Cancel</button>
            <button
              className="danger"
              disabled={typed !== device.displayName}
              onClick={() => {
                onWipe(
                  'wipe_device',
                  `FINAL CONFIRMATION — factory reset ${device.displayName}? This erases the device.`,
                  { confirmDeviceName: typed, payload: { wipeExternalStorage } },
                );
                close();
              }}
            >
              Wipe this device permanently
            </button>
          </div>

          <p className="small muted" style={{ marginBottom: 0 }}>
            Requires the <code>super_admin</code> role. The server checks the role and the typed
            name again, and records the attempt in the audit log whether it is allowed or refused.
          </p>
        </>
      )}
    </Panel>
  );
}

/**
 * Removes the dashboard record. Blocked when a named student is using an
 * online tablet. Confirmation is always "yes" so this cannot be a misclick.
 */
export function DeleteDevicePanel({
  device,
  onDeleted,
}: {
  device: Device;
  onDeleted: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const blocked = Boolean(device.student?.studentName) && device.online === 'online';

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/devices/${device.id}`, { confirm: typed.trim().toLowerCase() });
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete this device.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Remove from dashboard">
      {blocked ? (
        <Notice kind="warning">
          <strong>This tablet cannot be deleted while it is assigned and online.</strong>
          <div>
            It is assigned to {device.student?.studentName} and is currently checking in.
            Release the assignment or wait until it is offline.
          </div>
        </Notice>
      ) : (
        <>
          <p className="small muted" style={{ marginTop: 0 }}>
            Removes this device from the dashboard. The tablet is <strong>not</strong> factory
            reset. Use this for unassigned, offline or stale records.
          </p>
          {!open ? (
            <button className="danger" type="button" onClick={() => setOpen(true)}>
              Delete device record…
            </button>
          ) : (
            <>
              {error && <Notice kind="error">{error}</Notice>}
              <label>
                <span>Type <code>yes</code> to confirm</span>
                <input
                  type="text"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder="yes"
                />
              </label>
              <div className="actions">
                <button type="button" onClick={() => { setOpen(false); setTyped(''); }}>Cancel</button>
                <button
                  className="danger"
                  type="button"
                  disabled={busy || typed.trim().toLowerCase() !== 'yes'}
                  onClick={() => void submit()}
                >
                  {busy ? 'Deleting…' : 'Delete record'}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </Panel>
  );
}
